import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { styled } from "@mui/material/styles";
import MuiButton from "@mui/material/Button";
import { Field, reduxForm, formValueSelector } from "redux-form";
import * as yup from "yup";
import { useComparisonLogic } from "./useComparisonLogic";
import { AddressField, SubmitButton } from "./form/InputFields";
import { useDispatch, useSelector } from "src/store";
import {
  getBluebonnetPlan,
  setAddressInStore,
  setShowBaseGraphButtonInStore,
  setShowRateGraphInStore,
  setUsageInStore,
} from "src/slices/bkvReducer";
import { makeStyles } from "@material-ui/core";
import { competitorArray1 } from "src/constants";
import { useParams } from "react-router";
import SubText from "./SubText";
import SimpleText from "./SimpleText";
import { RootState } from "src/store";

const validationSchema = yup.object().shape({
  address: yup.string().nullable().required(),
});

const validate = (values) => {
  const formErrors = {};

  try {
    validationSchema.validateSync(values, { abortEarly: false });
  } catch (errors) {
    errors.inner.forEach((error) => {
      formErrors[error.path] = error.message;
    });
  }

  return formErrors;
};

let AddressBox = ({ handleOpen, content, onPersonalize, ...props }) => {
  const { handleSubmit } = props;
  const dispatch = useDispatch();
  const state = useSelector((state: RootState) => state.bkv);
  const theme = useTheme();
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const classes = useStyles();
  const urlParam = useParams();
  const searchParams = new URLSearchParams(window.location.search);
  const terms = searchParams.get("terms") ?? 12;
  const promocode = "SEOEIQ0001";

  const handleComparisonLogic = (compareType) => {
    // console.log("Comparison type:", compareType);
  };

  const onSubmit = () => {
    // Call the onPersonalize function passed from the parent
    onPersonalize();

    let is_competitor = false;
    if (competitorArray1.includes(urlParam.planType)) {
      is_competitor = true;
    }
    dispatch(
      getBluebonnetPlan(
        urlParam.planType,
        Number(state.address.zipcode),
        terms,
        is_competitor,
        promocode
      )
    );
    dispatch(setUsageInStore({}));
    dispatch(setShowBaseGraphButtonInStore(true));
    dispatch(setShowRateGraphInStore(true));

    // Run the comparison logic
    const graphText = content?.details?.description[0]?.graph_text;
    if (typeof graphText === "string") {
      const match = graphText.match(/vs\. (.*)/);
      if (match && match[1]) {
        const compareType = match[1];
        handleComparisonLogic(compareType);
      } else {
        console.warn("Could not extract compare type from graph text");
      }
    } else {
      console.warn("Graph text is not a string");
    }

    // Open the usage modal
    handleOpen("usageModal");

    setTimeout(() => {
      const btn = document.getElementById("personalized_savings_btn");
      if (btn) {
        btn.scrollIntoView({ block: "end", behavior: "smooth" });
      }
    }, 100);
  };

  const Button = styled(MuiButton)((props) => ({
    borderRadius: 50,
    padding: "1.6%",
    color: theme.palette.white.main,
  }));

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          padding: {
            xs: "7% 0% 2% 0%",
            sm: "7% 0% 2% 0%",
            md: "7% 3% 2% 3%",
          },
          flexDirection: "column",
        }}
      >
        <SubText text={content?.details?.address?.title} />
        <Box
          sx={{
            whiteSpace: { xs: "wrap", sm: "wrap", md: "wrap", lg: "nowrap" },
            boxShadow: "0px 0px 15px 0px rgba(0, 0, 0, 0.15)",
            borderRadius: { xs: "5vw", sm: "5vw", md: "5vw", lg: "20px" },
            position: "relative",
            boxSizing: "border-box",
            padding: {
              xs: "5% 8% 5% 8%",
              sm: "5% 8% 5% 8%",
              md: "5% 10% 5% 10%",
              lg: "20px 166px",
            },
            display: "flex",
            alignItems: "center",
            width: "100%",
            flexDirection: "column",
          }}
        >
          {/* <SimpleText
            style={{
              fontSize: {
                xs: "13px",
                md: "3.5vw",
                lg: "20px",
              },
              textAlign: "center",
              marginBottom: { xs: "5%", sm: "5%", mb: "5%", lg: "0" },
              color: "#2f2f2f",
            }}
            text={content?.details?.address?.title}
          /> */}
          <Box
            width="100%"
            sx={{
              display: "flex",
              justifyContent: "center",
              flexDirection: {
                lg: "row",
                md: "row",
                sm: "column",
                xs: "column",
              },
              marginTop: "2%",
              gap: {
                xs: "12px",
                md: "28px",
              },
            }}
          >
            <AddressField
              width={100}
              value={search}
              setValue={setSearch}
              placeholder={content?.details?.address?.placeholder}
              defaultValue={state.address}
              takeMargin={false}
              handleSelect={(address) => dispatch(setAddressInStore(address))}
            />
            <Button
              disabled={!state.address || state.elementLoading["address"]}
              className={classes.button}
              sx={{
                padding: {
                  xs: "3% 15%",
                  sm: "3% 15%",
                  md: "2% 15%",
                  lg: "13px 28px",
                },
                minWidth: "190px",
                backgroundColor: loading
                  ? theme.palette.action.disabled
                  : theme.palette.secondary.main,
                "&:hover": {
                  backgroundColor: (theme) => theme.palette.secondary.dark,
                },
              }}
              type="submit"
            >
              <Typography
                fontSize={{ xs: "12px", md: "3vw", lg: "15px" }}
                sx={{ lineHeight: "21px", fontWeight: "1000" }}
              >
                Personalize
              </Typography>
            </Button>
          </Box>
        </Box>
      </Box>
    </form>
  );
};

AddressBox = reduxForm({
  form: "AddressBox",
  validate,
  initialValues: {
    address: "",
  },
})(AddressBox);

const selector = formValueSelector("AddressBox");
export default AddressBox;

const useStyles = makeStyles((theme) => ({
  button: {
    transition: "all .1s ease-in-out",
    border: "3px solid #009A88",
    "&:hover": {
      background: "transparent",
      animation: "$scale .2s ease-in-out",
      color: "#009A88",
    },
  },
  "@keyframes scale": {
    "0%": {
      animation: "$scale .2s ease-in-out",
    },
    "50%": {
      transform: "scale(1.03, 1.03)",
    },
    "100%": {
      transform: "scale(1)",
    },
  },
}));
